import React, { useState, useRef, useEffect, useMemo } from 'react';
import './SearchableSelect.css';

/**
 * SearchableSelect
 * A reusable search & select dropdown component.
 *
 * Props:
 * - options: Array of items (objects or primitives)
 * - value: Currently selected item ID or value (string | number)
 * - onChange: Callback when selection changes: (value, item) => void
 * - placeholder: Text shown when nothing is selected (default: "Select...")
 * - searchPlaceholder: Placeholder for the search input (default: "Type to search...")
 * - allOptionLabel: Optional label for the "All" reset option (e.g. "All Customers")
 * - getOptionLabel: Function to extract display label from option
 * - getOptionValue: Function to extract value / ID from option
 * - getOptionSubtext: Function to extract secondary text (e.g. phone, status, code)
 * - filterOption: Custom filter function: (option, searchTerm) => boolean
 * - disabled: Boolean
 * - clearable: Boolean (default: true)
 * - id: String
 * - name: String
 * - className: String
 * - style: Object
 */
function SearchableSelect({
  options = [],
  value = '',
  onChange,
  placeholder = 'Select an option...',
  searchPlaceholder = 'Type to search...',
  allOptionLabel = null,
  getOptionLabel = (opt) => opt?.name || opt?.label || opt?.title || String(opt || ''),
  getOptionValue = (opt) => (opt?.id !== undefined ? opt.id : opt?.value !== undefined ? opt.value : opt),
  getOptionSubtext = null,
  filterOption = null,
  disabled = false,
  clearable = true,
  id,
  name,
  className = '',
  style = {}
}) {
  const [isOpen, setIsOpen] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [highlightedIndex, setHighlightedIndex] = useState(-1);

  const containerRef = useRef(null);
  const searchInputRef = useRef(null);
  const listRef = useRef(null);

  // Find the currently selected option object
  const selectedOption = useMemo(() => {
    if (value === '' || value === null || value === undefined) return null;
    return options.find((opt) => String(getOptionValue(opt)) === String(value)) || null;
  }, [options, value, getOptionValue]);

  // Filter options based on searchTerm
  const filteredOptions = useMemo(() => {
    if (!searchTerm.trim()) return options;
    const term = searchTerm.toLowerCase().trim();

    if (filterOption) {
      return options.filter((opt) => filterOption(opt, term));
    }

    return options.filter((opt) => {
      const label = String(getOptionLabel(opt) || '').toLowerCase();
      const subtext = getOptionSubtext ? String(getOptionSubtext(opt) || '').toLowerCase() : '';
      return label.includes(term) || subtext.includes(term);
    });
  }, [options, searchTerm, filterOption, getOptionLabel, getOptionSubtext]);

  // Close when clicking outside
  useEffect(() => {
    const handleClickOutside = (e) => {
      if (containerRef.current && !containerRef.current.contains(e.target)) {
        setIsOpen(false);
        setSearchTerm('');
        setHighlightedIndex(-1);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  // Autofocus search input when opened
  useEffect(() => {
    if (isOpen) {
      setHighlightedIndex(-1);
      const timer = setTimeout(() => {
        if (searchInputRef.current) {
          searchInputRef.current.focus();
        }
      }, 30);
      return () => clearTimeout(timer);
    } else {
      setSearchTerm('');
    }
  }, [isOpen]);

  const handleSelect = (val, opt) => {
    if (onChange) {
      onChange(val, opt);
    }
    setIsOpen(false);
    setSearchTerm('');
    setHighlightedIndex(-1);
  };

  const handleClear = (e) => {
    e.stopPropagation();
    if (onChange) {
      onChange('', null);
    }
    setSearchTerm('');
  };

  const toggleDropdown = () => {
    if (disabled) return;
    setIsOpen((prev) => !prev);
  };

  // Keyboard navigation
  const handleKeyDown = (e) => {
    if (disabled) return;

    if (!isOpen) {
      if (e.key === 'ArrowDown' || e.key === 'ArrowUp' || e.key === 'Enter' || e.key === ' ') {
        e.preventDefault();
        setIsOpen(true);
      }
      return;
    }

    const totalItems = (allOptionLabel ? 1 : 0) + filteredOptions.length;

    if (e.key === 'ArrowDown') {
      e.preventDefault();
      setHighlightedIndex((prev) => (prev + 1 < totalItems ? prev + 1 : 0));
    } else if (e.key === 'ArrowUp') {
      e.preventDefault();
      setHighlightedIndex((prev) => (prev - 1 >= 0 ? prev - 1 : totalItems - 1));
    } else if (e.key === 'Enter') {
      e.preventDefault();
      if (highlightedIndex >= 0) {
        if (allOptionLabel && highlightedIndex === 0) {
          handleSelect('', null);
        } else {
          const optIndex = allOptionLabel ? highlightedIndex - 1 : highlightedIndex;
          if (filteredOptions[optIndex]) {
            const opt = filteredOptions[optIndex];
            handleSelect(getOptionValue(opt), opt);
          }
        }
      }
    } else if (e.key === 'Escape') {
      e.preventDefault();
      setIsOpen(false);
      setSearchTerm('');
    }
  };

  // Scroll highlighted item into view
  useEffect(() => {
    if (isOpen && highlightedIndex >= 0 && listRef.current) {
      const items = listRef.current.querySelectorAll('.searchable-select-item');
      if (items[highlightedIndex]) {
        items[highlightedIndex].scrollIntoView({ block: 'nearest' });
      }
    }
  }, [highlightedIndex, isOpen]);

  const hasValue = value !== '' && value !== null && value !== undefined;
  const displayLabel = selectedOption
    ? getOptionLabel(selectedOption)
    : hasValue
    ? String(value)
    : '';

  const listId = id ? `${id}-list` : 'searchable-select-list';

  return (
    <div
      className={`searchable-select-wrapper ${isOpen ? 'is-open' : ''} ${disabled ? 'is-disabled' : ''} ${className}`}
      style={style}
      ref={containerRef}
      onKeyDown={handleKeyDown}
      id={id ? `${id}-container` : undefined}
    >
      <div
        className="searchable-select-trigger"
        onClick={toggleDropdown}
        tabIndex={disabled ? -1 : 0}
        role="combobox"
        aria-expanded={isOpen}
        aria-controls={listId}
        aria-haspopup="listbox"
        id={id}
      >
        <div className="searchable-select-display">
          {hasValue ? (
            <span className="searchable-select-value-text">{displayLabel}</span>
          ) : (
            <span className="searchable-select-placeholder">{allOptionLabel || placeholder}</span>
          )}
        </div>

        <div className="searchable-select-actions">
          {clearable && hasValue && !disabled && (
            <button
              type="button"
              className="searchable-select-clear"
              onClick={handleClear}
              title="Clear selection"
              aria-label="Clear selection"
            >
              ✕
            </button>
          )}
          <span className={`searchable-select-arrow ${isOpen ? 'open' : ''}`}>▾</span>
        </div>
      </div>

      {isOpen && (
        <div className="searchable-select-popover">
          <div className="searchable-select-search-box">
            <span className="searchable-select-search-icon">🔍</span>
            <input
              ref={searchInputRef}
              type="text"
              name={name ? `${name}_search` : undefined}
              className="searchable-select-search-input"
              value={searchTerm}
              onChange={(e) => {
                setSearchTerm(e.target.value);
                setHighlightedIndex(0);
              }}
              placeholder={searchPlaceholder}
              onClick={(e) => e.stopPropagation()}
              autoComplete="off"
            />
            {searchTerm && (
              <button
                type="button"
                className="searchable-select-search-clear"
                onClick={(e) => {
                  e.stopPropagation();
                  setSearchTerm('');
                  searchInputRef.current?.focus();
                }}
                title="Clear search"
              >
                ✕
              </button>
            )}
          </div>

          <div className="searchable-select-list" id={listId} ref={listRef} role="listbox">
            {allOptionLabel && (
              <div
                className={`searchable-select-item searchable-select-all-item ${!hasValue ? 'selected' : ''} ${highlightedIndex === 0 ? 'highlighted' : ''}`}
                onClick={() => handleSelect('', null)}
                role="option"
                aria-selected={!hasValue}
              >
                <div className="searchable-select-item-content">
                  <span className="searchable-select-item-title">{allOptionLabel}</span>
                </div>
                {!hasValue && <span className="searchable-select-check">✓</span>}
              </div>
            )}

            {filteredOptions.length > 0 ? (
              filteredOptions.map((opt, idx) => {
                const optVal = getOptionValue(opt);
                const isSelected = String(optVal) === String(value);
                const itemIndex = allOptionLabel ? idx + 1 : idx;
                const isHighlighted = highlightedIndex === itemIndex;
                const label = getOptionLabel(opt);
                const subtext = getOptionSubtext ? getOptionSubtext(opt) : null;

                return (
                  <div
                    key={optVal !== undefined && optVal !== null ? optVal : idx}
                    className={`searchable-select-item ${isSelected ? 'selected' : ''} ${isHighlighted ? 'highlighted' : ''}`}
                    onClick={() => handleSelect(optVal, opt)}
                    onMouseEnter={() => setHighlightedIndex(itemIndex)}
                    role="option"
                    aria-selected={isSelected}
                  >
                    <div className="searchable-select-item-content">
                      <span className="searchable-select-item-title">{label}</span>
                      {subtext && (
                        <span className="searchable-select-item-subtext">{subtext}</span>
                      )}
                    </div>
                    {isSelected && <span className="searchable-select-check">✓</span>}
                  </div>
                );
              })
            ) : (
              <div className="searchable-select-empty">
                <span>No results found{searchTerm ? ` for "${searchTerm}"` : ''}</span>
              </div>
            )}
          </div>

          {filteredOptions.length > 5 && (
            <div className="searchable-select-footer">
              <span>{filteredOptions.length} option{filteredOptions.length === 1 ? '' : 's'} available</span>
            </div>
          )}
        </div>
      )}
    </div>
  );
}

export default SearchableSelect;
